import { useEffect, useMemo, useState } from "react";
import {
  buildPendingDashboardItems,
  type PendingActionItem,
} from "../utils/pendingDashboardActions";
import {
  getPendingDashboardBootstrap,
  type PendingDashboardBundle,
} from "../utils/pendingDashboardBootstrap";

function rowsFromBundle(bundle: PendingDashboardBundle | null): {
  profile: any;
  orderRows: any[];
  quoteRows: any[];
} {
  if (!bundle) {
    return { profile: null, orderRows: [], quoteRows: [] };
  }
  const ok = (v: any) => v && (v.status === true || v.status === "true");
  const profile = ok(bundle.profile) ? bundle.profile?.data ?? null : null;
  const orderRows = ok(bundle.orders) ? bundle.orders?.data?.rows ?? [] : [];
  const quoteRows = ok(bundle.quotes) ? bundle.quotes?.data?.rows ?? [] : [];
  return { profile, orderRows, quoteRows };
}

/**
 * Loads profile, orders, and saved quote drafts (cached + parallel); derives dashboard pending-action cards.
 */
export function usePendingDashboardActions(): {
  items: PendingActionItem[];
  loading: boolean;
} {
  const [bundle, setBundle] = useState<PendingDashboardBundle | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void getPendingDashboardBootstrap()
      .then((res) => {
        if (!cancelled) setBundle(res);
      })
      .catch(() => {
        if (!cancelled) setBundle(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo(() => {
    if (loading) return [];
    const { profile, orderRows, quoteRows } = rowsFromBundle(bundle);
    return buildPendingDashboardItems({
      profile,
      orderRows,
      draftQuoteRows: quoteRows,
    });
  }, [loading, bundle]);

  return { items, loading };
}
